import Phaser from 'phaser';
import type { AdventureSceneDefinition, HotspotDefinition, HotspotShape } from '../../domain/types';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import { getGameRuntime } from '../runtime';
import { TownSquarePresentation } from './TownSquarePresentation';

interface AdventureSceneData {
  sceneId: string;
}

export class AdventureScene extends Phaser.Scene {
  #definition!: AdventureSceneDefinition;
  #debugGraphics?: Phaser.GameObjects.Graphics;
  #soldierArm?: Phaser.GameObjects.Rectangle;
  #knife?: Phaser.GameObjects.Image;
  #runtimeHotspots: HotspotDefinition[] = [];
  #uiInputBlocked = false;
  #animationBounds?: Phaser.GameObjects.Graphics;
  #townSquare?: TownSquarePresentation;
  #hotspotZones: { hotspot: HotspotDefinition; zone: Phaser.GameObjects.Zone }[] = [];

  constructor() {
    super('Adventure');
  }

  init(data: AdventureSceneData): void {
    this.#soldierArm = undefined;
    this.#knife = undefined;
    this.#animationBounds = undefined;
    this.#townSquare = undefined;
    this.#hotspotZones = [];
    this.#definition = getGameRuntime().getScene(data.sceneId);
    this.#runtimeHotspots = structuredClone(this.#definition.hotspots);
  }

  create(): void {
    const runtime = getGameRuntime();
    this.cameras.main.fadeIn(360, 8, 20, 18);
    this.add
      .image(GAME_WIDTH / 2, GAME_HEIGHT / 2, this.#definition.backgroundAssetId)
      .setDisplaySize(GAME_WIDTH, GAME_HEIGHT);

    if (this.#definition.id === 'raadhustorvet-scene') {
      this.#townSquare = new TownSquarePresentation(
        this,
        runtime.store.snapshot.settings.reducedMotion,
      );
    } else {
      this.#createLivingLayers();
      this.#createKnife();
    }
    this.#createHotspots();
    this.#refreshState();

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      runtime.events.emit('debug:pointer', {
        x: Math.round(pointer.worldX),
        y: Math.round(pointer.worldY),
        normalizedX: Number((pointer.worldX / GAME_WIDTH).toFixed(4)),
        normalizedY: Number((pointer.worldY / GAME_HEIGHT).toFixed(4)),
      });
    });

    const cleanups = [
      runtime.events.on('animation:command', ({ animationId }) => {
        if (animationId === 'soldier-stop-carving' && this.#soldierArm) {
          this.tweens.killTweensOf(this.#soldierArm);
          this.#soldierArm.setRotation(-0.18);
        }
      }),
      runtime.events.on('ui:inputBlocked', ({ blocked }) => {
        this.#uiInputBlocked = blocked;
      }),
      runtime.events.on('ui:debugAnimationBounds', ({ visible }) => {
        this.#animationBounds?.setVisible(visible);
      }),
      runtime.events.on('ui:debugHotspotChanged', ({ hotspot }) => {
        const index = this.#runtimeHotspots.findIndex((entry) => entry.id === hotspot.id);
        if (index >= 0) {
          this.#runtimeHotspots[index] = structuredClone(hotspot);
          this.#definition.hotspots[index] = structuredClone(hotspot);
        }
        this.scene.restart({ sceneId: this.#definition.id });
      }),
      runtime.store.subscribe(() => this.#refreshState()),
    ];
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => cleanups.forEach((cleanup) => cleanup()));

    runtime.events.emit('scene:adventure', this.#definition);
    runtime.events.emit('scene:hotspots', this.#visibleHotspots());
    runtime.events.emit('audio:theme', { theme: this.#townSquare ? 'map' : 'forest' });
  }

  #createLivingLayers(): void {
    const runtime = getGameRuntime();
    const reduced = runtime.store.snapshot.settings.reducedMotion;

    const leaves = this.add.ellipse(1540, 120, 720, 250, 0x85a867, 0.18).setDepth(20);
    const smoke = this.add.ellipse(490, 650, 90, 160, 0xdde4da, 0.22).setDepth(15);
    const flame = this.add.triangle(500, 850, 0, 80, 46, 0, 92, 80, 0xf39a43, 0.82).setDepth(14);
    this.#soldierArm = this.add
      .rectangle(938, 555, 155, 24, 0x7d6650, 0.9)
      .setRotation(0.18)
      .setDepth(16);
    if (runtime.debugHotspots) {
      this.#animationBounds = this.add.graphics().setDepth(52).setVisible(false);
      this.#animationBounds.lineStyle(4, 0xff6bd6, 0.95);
      this.#animationBounds.strokeRect(1180, 0, 720, 250);
      this.#animationBounds.strokeRect(430, 570, 130, 260);
      this.#animationBounds.strokeRect(845, 500, 200, 110);
    }

    if (!reduced) {
      this.tweens.add({
        targets: leaves,
        x: '+=28',
        angle: 2,
        yoyo: true,
        repeat: -1,
        duration: 2600,
        ease: 'Sine.easeInOut',
      });
      this.tweens.add({
        targets: [smoke],
        y: '-=70',
        alpha: 0.04,
        scale: 1.3,
        yoyo: true,
        repeat: -1,
        duration: 1900,
      });
      this.tweens.add({
        targets: flame,
        scaleY: 0.7,
        alpha: 0.62,
        yoyo: true,
        repeat: -1,
        duration: 420,
      });
      if (!runtime.store.snapshot.flags.soldierKnifeAvailable) {
        this.tweens.add({
          targets: this.#soldierArm,
          angle: -12,
          yoyo: true,
          repeat: -1,
          duration: 620,
          ease: 'Sine.easeInOut',
        });
      }
    }
  }

  #createKnife(): void {
    this.#knife = this.add.image(1210, 735, 'knife-icon').setDisplaySize(170, 74).setDepth(17);
  }

  #createHotspots(): void {
    const runtime = getGameRuntime();
    this.#debugGraphics = this.add.graphics().setDepth(50);

    for (const hotspot of this.#runtimeHotspots) {
      const zone = this.#zoneFor(hotspot.shape);
      zone.setInteractive({ useHandCursor: true });
      this.#hotspotZones.push({ hotspot, zone });
      zone.on('pointerover', (pointer: Phaser.Input.Pointer) => {
        runtime.events.emit('hotspot:hover', {
          label: hotspot.label,
          x: pointer.worldX,
          y: pointer.worldY,
        });
      });
      zone.on('pointerout', () => runtime.events.emit('hotspot:leave'));
      zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        if (this.#uiInputBlocked || !(pointer.event?.target instanceof HTMLCanvasElement)) return;
        const itemRelevant = hotspot.actions.filter((action) => {
          if (action.itemId && runtime.store.snapshot.selectedItemId) {
            return action.itemId === runtime.store.snapshot.selectedItemId;
          }
          return !runtime.store.snapshot.selectedItemId || action.verb === 'use';
        });
        const actions = itemRelevant.filter(
          (action) =>
            runtime.evaluator.evaluateAll(action.conditions, runtime.store.snapshot) ||
            Boolean(action.unavailableMessage),
        );
        runtime.events.emit('hotspot:actions', {
          hotspot,
          actions: actions.length ? actions : itemRelevant,
          x: pointer.worldX,
          y: pointer.worldY,
        });
      });
      if (runtime.debugHotspots) this.#drawDebugShape(hotspot);
    }
  }

  #zoneFor(shape: HotspotShape): Phaser.GameObjects.Zone {
    if (shape.type === 'rect') {
      return this.add.zone(shape.x, shape.y, shape.width, shape.height).setOrigin(0).setDepth(40);
    }
    const zone = this.add.zone(0, 0, GAME_WIDTH, GAME_HEIGHT).setOrigin(0).setDepth(40);
    const polygon = new Phaser.Geom.Polygon(shape.points);
    zone.setInteractive(polygon, Phaser.Geom.Polygon.Contains);
    return zone;
  }

  #drawDebugShape(hotspot: HotspotDefinition): void {
    if (!this.#debugGraphics) return;
    const color = Phaser.Display.Color.HexStringToColor(hotspot.debugColor ?? '#f5c45e').color;
    this.#debugGraphics.lineStyle(5, color, 0.95).fillStyle(color, 0.13);
    if (hotspot.shape.type === 'rect') {
      const { x, y, width, height } = hotspot.shape;
      this.#debugGraphics.fillRect(x, y, width, height).strokeRect(x, y, width, height);
    } else {
      const points = hotspot.shape.points.map((point) => new Phaser.Math.Vector2(point.x, point.y));
      this.#debugGraphics.fillPoints(points, true).strokePoints(points, true);
    }
    const anchor = hotspot.shape.type === 'rect' ? hotspot.shape : hotspot.shape.points[0];
    if (anchor) {
      this.add
        .text(anchor.x, anchor.y, `${hotspot.id}\n${hotspot.label}`, {
          fontFamily: 'monospace',
          fontSize: '20px',
          backgroundColor: '#07131e',
          color: '#ffffff',
          padding: { x: 6, y: 4 },
        })
        .setDepth(51);
    }
  }

  #visibleHotspots(): HotspotDefinition[] {
    const runtime = getGameRuntime();
    return this.#runtimeHotspots.filter((hotspot) =>
      runtime.evaluator.evaluateAll(hotspot.visibleConditions, runtime.store.snapshot),
    );
  }

  #refreshState(): void {
    const runtime = getGameRuntime();
    this.#townSquare?.refresh(runtime.store.snapshot);
    for (const { hotspot, zone } of this.#hotspotZones) {
      // Skjulte genstande må heller ikke kunne vælges med musen efter et bytte.
      if (zone.input)
        zone.input.enabled =
          runtime.evaluator.evaluateAll(hotspot.visibleConditions, runtime.store.snapshot) &&
          runtime.evaluator.evaluateAll(hotspot.enabledConditions, runtime.store.snapshot);
    }
    const knifeTaken = runtime.store.snapshot.inventory.includes('austrianSoldierKnife');
    this.#knife?.setVisible(!knifeTaken);
    if (runtime.store.snapshot.flags.soldierKnifeAvailable) {
      this.#soldierArm?.setRotation(-0.18);
    }
    runtime.events.emit('scene:hotspots', this.#visibleHotspots());
  }
}
